import { Injectable, NgZone, OnDestroy } from "@angular/core";
import {
	AppState,
	CheckCache,
	CheckCacheSeance,
	CheckInstalledVersion,
	CheckMobilePlanningCache,
	CheckupStackService,
	ChronoService,
	DataIsInitialized,
	DataLoadedFromBack,
	GetRemplacantSessions,
	InitLocalData,
	InitNetwork,
	InitSesam,
	LoadIdModification,
	Logger,
	LogManager,
	Logout,
	MobilePreferenceHelperService,
	NetworkService,
	NetworkStateService,
	PollingLoopService,
	PurgeData,
	PushInit,
	PushWatchdogService,
	selectAbonnementComptaActif,
	selectAbonnementGarantiePaiementActif,
	selectAllMobilePreferences,
	selectAllMobilePreferencesRemplacant,
	selectCacheChecked,
	selectCacheCleared,
	selectCurrentClient,
	selectCurrentConfig,
	selectGroupEntityInited,
	selectIsAuthenticated,
	selectIsDataReady,
	selectLoadGroupEntityError,
	selectNetworkAvailable,
	selectProfileLoaded,
	selectPushLoaded,
	selectPushLoading,
	selectStoreHydrated,
	selectVersionChecked,
	StartupInterface,
} from "features";
import { environment } from "@env/environment";
import { MobileClient, MobileConfig, MobileServiceCba } from "@jack-sparrow-libraries/emotion-models";
import { select, Store } from "@ngrx/store";
import { combineLatest, Subject, Subscription } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";
import { LoadingAyWebService } from "./loading-ay-web.service";
import { LoadCommunicationByPsId } from "../store/communication/communication.action";
import { ConfigUtils } from "../utils/config.utils";
import { ObservableUtils } from "../utils/ObservableUtils";
import { ClientFacadeService } from "@services/facades/client.facade";
import { Actions } from "@ngrx/effects";

@Injectable({ providedIn: "root" })
export class StartupService implements OnDestroy, StartupInterface {
	initialized: boolean = false;
	logger: Logger;
	loader: any;
	resumeSubForLock: Subscription;
	config: MobileConfig;
	client: MobileClient;
	applicationVersion;
	applicationName;
	networkAvailable: boolean;
	appEventsinit: boolean = false;
	private abonnementCompta: MobileServiceCba;
	private abonnementGarantiePaiement: MobileServiceCba;
	private sub: Subscription;
	public runned = new Subject<boolean>();

	constructor(
		private chrono: ChronoService,
		private store: Store<AppState>,
		private networkService: NetworkService,
		private gardeFou: PushWatchdogService,
		private logManager: LogManager,
		private loadingService: LoadingAyWebService,
		private zone: NgZone,
		private pollingService: PollingLoopService,
		private preferenceHelper: MobilePreferenceHelperService,
		private checkupStackService: CheckupStackService,
		private networkStateService: NetworkStateService,
		private clientFacadeService: ClientFacadeService,
		private actions$: Actions,
	) {
		this.chrono.addEvent(new Date().getTime(), "constructeur");
		this.sub = combineLatest([
			this.store.select(selectAbonnementComptaActif),
			this.store.select(selectAbonnementGarantiePaiementActif),
			this.store.select(selectNetworkAvailable),
			this.store.select(selectCurrentClient),
		]).subscribe(([abonnementCompta, abonnementGarantiePaiement, networkAvailable, client]) => {
			this.abonnementGarantiePaiement = abonnementGarantiePaiement;
			this.abonnementCompta = abonnementCompta;
			this.networkAvailable = networkAvailable;
			this.client = client;
		});
	}

	unLogged() {
		console.error("NO AUTH");
	}

	async run(firstLogin: boolean, passChanged: boolean = false) {
		if (this.initialized) {
			this.runned.next(true);
			return;
		}
		await this.waitForAuth();

		this.config = await this.checkConfig(firstLogin);

		this.applicationVersion = environment.version;
		this.applicationName = environment.typeApp;

		this.networkAvailable = await this.initAppEvents();

		const logout = await this.initRevolution();
		if (!logout) {
			this.pollingService.run();
		}
		this.runned.next(true);
	}

	async waitForAuth() {
		const promise: Promise<any> = new Promise((resolve, reject) => {
			this.chrono.addEvent(new Date().getTime(), "loadProfile or Signin");

			let sub: Subscription = null;
			sub = this.store
				.pipe(
					select(selectIsAuthenticated),
					filter((zuh) => !!zuh),
					take(1),
				)
				.subscribe(() => {
					if (sub) {
						sub.unsubscribe();
					}

					this.chrono.addEvent(new Date().getTime(), "FIN loadProfile or Signin");

					resolve(true);
				});
		});

		await promise;
	}

	isAuthenticated(): Promise<boolean> {
		return this.store
			.pipe(
				select(selectStoreHydrated),
				filter((x) => !!x),
				switchMap((x) => this.store.select(selectProfileLoaded)),
				filter((x) => !!x),
				switchMap((x) => this.store.select(selectIsAuthenticated)),
				take(1),
			)
			.toPromise();
	}

	waitForStoreHydratation() {
		return this.store
			.pipe(
				select(selectStoreHydrated),
				filter((hydrated) => !!hydrated),
				take(1),
				map(() => {}),
			)
			.toPromise();
	}

	async initAppEvents(): Promise<boolean> {
		if (!this.appEventsinit) {
			// init network service
			this.networkService.init();
		}
		this.store.dispatch(new InitNetwork(this.networkService.isOnline));
		// this.networkStateService.pingNetwork();

		// Fix pour le deco / reco => sub multiple sinon
		if (!this.appEventsinit) {
			this.networkService.onlineEvent.subscribe(() => {
				this.chrono.addEvent(new Date().getTime(), `NETWORK onConnect() }`);
				setTimeout(
					() =>
						this.zone.run(() => {
							console.log("SetNetworkAvailable TRUE");
							console.log(this.networkService.type);
							if (this.networkService.isOnline) {
								// this.store.dispatch(new SetNetworkAvailable(true));
								this.networkStateService.pingNetwork();
							}
						}),
					1000,
				);
			});

			this.networkService.offlineEvent.subscribe(() => {
				this.chrono.addEvent(new Date().getTime(), `NETWORK onDisonnect() }`);
				this.zone.run(() => {
					console.log("SetNetworkAvailable FALSE");
					console.log(this.networkService.type);

					// this.store.dispatch(new SetNetworkAvailable(false));
					this.networkStateService.pingNetwork();
				});
			});

			this.chrono.addEvent(new Date().getTime(), `initAppEvents : ${this.networkService.type}`);

			this.appEventsinit = true;
		}

		return new Promise((resolve, reject) => {
			let sub: Subscription = null;
			sub = this.store.select(selectNetworkAvailable).subscribe((network) => {
				if (sub) {
					sub.unsubscribe();
				}

				resolve(network);
			});
		});
	}

	async wasCacheCleared(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			let sub2: Subscription = null;
			sub2 = this.store.select(selectCacheCleared).subscribe((cacheCleared) => {
				if (sub2) {
					sub2.unsubscribe();
				}

				resolve(cacheCleared);
			});
		});
	}

	async waitForCacheChecked(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			this.chrono.addEvent(new Date().getTime(), "ASK FOR waitForCacheChecked");

			let sub: Subscription = null;
			sub = combineLatest([this.store.select(selectCacheChecked), this.store.select(selectLoadGroupEntityError)])
				.pipe(
					filter(([checked, error]) => !!checked || !!error),
					take(1),
				)
				.subscribe(([checked, error]) => {
					if (sub) {
						sub.unsubscribe();
					}
					this.chrono.addEvent(new Date().getTime(), "FIN waitForCacheChecked");
					resolve(error);
				});
		});
	}

	async checkCache(fromInit: boolean): Promise<{ logout?: boolean; cleared?: boolean }> {
		this.store.dispatch(new CheckCache(fromInit));
		const logout = await this.waitForCacheChecked();
		if (logout) {
			return { logout: true };
		}
		return { cleared: await this.wasCacheCleared() };
	}

	async checkInstalledVersion(fromInit): Promise<{ logout?: boolean; cleared?: boolean }> {
		this.store.dispatch(new CheckInstalledVersion(fromInit));
		const logout = await this.waitForCheckInstalledVersion();
		if (logout) {
			return { logout: true };
		}

		return { cleared: await this.wasCacheCleared() };
	}

	checkConfig(firstLogin: boolean): Promise<MobileConfig> {
		if (firstLogin) {
			this.store.dispatch(new LoadIdModification());
		}
		return new Promise((resolve, reject) => {
			this.chrono.addEvent(new Date().getTime(), "ASK FOR CHECK CONFIG");

			let sub: Subscription = null;
			sub = this.store
				.pipe(
					select(selectCurrentConfig),
					filter((config) => !!config),
					take(1),
				)
				.subscribe((config) => {
					// console.log('CONFIG !!!!!!!!!!!!!!!!!', data);

					this.chrono.addEvent(new Date().getTime(), "FIN CHECK CONFIG");

					if (sub) {
						sub.unsubscribe();
					}

					resolve(config);
				});
		});
	}

	async waitForCheckInstalledVersion(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			this.chrono.addEvent(new Date().getTime(), "ASK FOR CheckInstalledVersion");

			let sub: Subscription = null;
			sub = combineLatest([
				this.store.select(selectVersionChecked),
				this.store.select(selectLoadGroupEntityError),
			])
				.pipe(
					filter(([checked, error]) => !!checked || !!error),
					take(1),
				)
				.subscribe(([checked, error]) => {
					if (sub) {
						sub.unsubscribe();
					}
					this.chrono.addEvent(new Date().getTime(), "FIN CheckInstalledVersion");
					resolve(error);
				});
		});
	}

	async initRevolution(): Promise<boolean> {
		if (this.networkAvailable) {
			let rep = await this.checkInstalledVersion(true);
			if (rep.cleared !== undefined && !rep.cleared) {
				rep = await this.checkCache(true);
			}

			if (rep.logout === undefined) {
				rep.logout = await this.waitForDataSynchro();
			}

			if (rep.logout !== undefined && rep.logout) {
				return await this.logout();
			}
		}

		this.store.dispatch(new DataIsInitialized());
		if (this.preferenceHelper.getPreferences().length === 0) {
			this.preferenceHelper.setPreferences(
				await this.store.select(selectAllMobilePreferences).pipe(take(1)).toPromise(),
			);
		}
		if (this.preferenceHelper.getPreferenceRemplacant() === undefined) {
			this.preferenceHelper.setPreferenceRemplacant(
				await this.store.select(selectAllMobilePreferencesRemplacant).pipe(take(1)).toPromise(),
			);
		}
		this.store.dispatch(new CheckCacheSeance());
		this.store.dispatch(new CheckMobilePlanningCache());
		this.store.dispatch(new PurgeData());
		this.store.dispatch(new InitSesam());
		this.initialized = true;
		// gestion des remplacants
		// REV-4825
		const theTrueConfig: MobileConfig = await ObservableUtils.select(this.store, selectCurrentConfig);
		this.store.dispatch(new LoadCommunicationByPsId(theTrueConfig.idPs));
		if (
			theTrueConfig?.remplacant &&
			!theTrueConfig?.remplacantSessionActive &&
			// !ConfigUtils.isAppCba(theTrueConfig)
			!ConfigUtils.isEnvironnementAppCba(environment)
		) {
			// On récupère le MobileConfig pour vérifier si le client est un remplacant auqual cas il faut selctionner une session
			this.store.dispatch(new GetRemplacantSessions({ changeTitulaire: false }));

			await this.store
				.select(selectCurrentConfig)
				.pipe(
					filter((config) => !!config?.idel),
					take(1),
				)
				.toPromise();
		}

		// garde fou du push
		this.gardeFou.run();

		this.logManager.init(environment.logging);
		this.logManager.purgeLog();
		this.logger = this.logManager.getLogger(this.constructor.name);
		return false;
	}

	async logout(): Promise<boolean> {
		await this.loadingService.hide();
		this.store.dispatch(new Logout());
		return true;
	}

	surveilleDataLoadedFromBack() {
		let sub: Subscription = null;
		sub = this.store
			.pipe(
				select(selectGroupEntityInited),
				filter((inited) => !!inited),
				take(1),
			)
			.subscribe(() => {
				if (sub) {
					sub.unsubscribe();
				}
				this.store.dispatch(new DataLoadedFromBack());
			});
	}

	waitForPushInit(): Promise<boolean> {
		this.store.dispatch(new PushInit());
		return ObservableUtils.getSavedStatus(
			this.store.select(selectPushLoading),
			this.store.select(selectPushLoaded),
		);
	}

	waitForDataSynchro(): Promise<boolean> {
		this.store.dispatch(new InitLocalData());
		this.surveilleDataLoadedFromBack();
		return new Promise((resolve, reject) => {
			let sub: Subscription = null;

			sub = combineLatest([this.store.select(selectIsDataReady), this.store.select(selectLoadGroupEntityError)])
				.pipe(
					filter(([checked, error]) => !!checked || !!error),
					take(1),
				)
				.subscribe(([checked, error]) => {
					if (sub) {
						sub.unsubscribe();
					}
					resolve(error);
				});
		});
	}

	setInitialized(val: boolean) {
		this.initialized = val;
	}

	ngOnDestroy(): void {
		if (this.resumeSubForLock) {
			this.resumeSubForLock.unsubscribe();
		}
	}
}
